import { Logout as LogoutIcon } from "@mui/icons-material";
import { Button } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useNavigate } from "@tanstack/react-router";
import { useAnalyticsTracker } from "~/app/hooks/analytics/useAnalyticsTracker";
import type { TCommodityKey } from "~/app/stores/commodities";
import { useCommoditiesStore } from "~/app/stores/commodities";
import { useNavigationStore } from "~/app/stores/navigation";
import iconDiamonds from "~/assets/commodities/diamonds.webp";
import iconHelpingHand from "~/assets/commodities/helping_hand.webp";
import iconHints from "~/assets/commodities/hints.webp";
import iconMultiplierFreeToken from "~/assets/commodities/multiplier_token.webp";
import iconSpinUpgradeToken from "~/assets/commodities/spin_token.webp";
import iconTournamentToken from "~/assets/commodities/tournament_token.webp";
import iconVowelDetector from "~/assets/commodities/vowel_blast.webp";
import iconPlus from "~/assets/commodities_plus.webp";
import { useAuthStore, useLogout, useUser } from "~/stores/auth";
import { formatNumber } from "~/utils/format";

type TPlayerProfileCommodities = Partial<Record<TCommodityKey, { icon: string; label: string }>>;

interface PlayerProfileProps {
  isLoading: boolean;
}

export function PlayerProfile({ isLoading }: PlayerProfileProps) {
  const { userData } = useAuthStore();
  const user = useUser();
  const logout = useLogout();
  const trackUI = useAnalyticsTracker("sys.ui");

  const commoditiesRenderingMap: TPlayerProfileCommodities = {
    Diamonds: { icon: iconDiamonds, label: "Diamonds" },
    SpinUpgradeToken: { icon: iconSpinUpgradeToken, label: "Super Spin Tokens" },
    MultiplierFreeToken: { icon: iconMultiplierFreeToken, label: "Multiplier Tokens" },
    HelpingHand: { icon: iconHelpingHand, label: "Helping Hands" },
    VowelDetector: { icon: iconVowelDetector, label: "Vowel Blasts" },
    Hints: { icon: iconHints, label: "Hints" },
    TournamentFreeEntryToken: { icon: iconTournamentToken, label: "Tournament Tokens" },
  };

  const navigate = useNavigate();
  const { setActiveStoreSectionAnchor  } = useNavigationStore();
  const { commoditiesBalance, isInventoryLoading } = useCommoditiesStore();

  const handleMoreCommoditiesClick = (commodity: TCommodityKey) => {
    trackUI({
      screen_id: commodity,
      screen_name: "web_portal",
      screen_type: "screen",
      screen_category: "web_portal",
      screen_subcategory: "web_portal",
      screen_content:
        commoditiesRenderingMap[commodity]?.label.toLowerCase().replace(/\s/g, "_") || "",
      screen_section: null,
      interaction_type: "tap",
      interaction_object: "commodity_button",
      interaction_object_category: "display",
      flow_name: "web_portal",
      flow_order: 2,
    });
    setActiveStoreSectionAnchor(commodity === "Diamonds" ? "diamonds" : "bundles");
    navigate({ to: "/store" });
  };

  const handleLogout = () => {
    logout({});
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Box position="relative">
        {isLoading ? (
          <Skeleton
            variant="circular"
            sx={{
              width: 96,
              height: 96,
              background: "#6B91D4",
            }}
          />
        ) : (
          <>
            <Avatar
              src={
                user?.PictureUrlMedium ||
                "https://d2e4h2ehb6h2cs.cloudfront.net/2025/03/07/07e78fc1-82a2-4ae1-b1a7-fc1f77cb0fb8.m.jpg"
              }
              alt=""
              sx={{
                width: 96,
                height: 96,
                border: 4,
                borderColor: "white",
                boxShadow: 3,
              }}
            />

            <IconButton
              onClick={handleLogout}
              sx={{
                position: "absolute",
                bottom: -8,
                right: 0,
                backgroundColor: "error.light",
                padding: "4px",
                boxShadow: 2,
                "&:hover": {
                  backgroundColor: "error.main",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: 16,
                  color: "white",
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </>
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={0.5}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: "28px",
            fontWeight: "900",
            lineHeight: "28px",
            letterSpacing: "-0.56px",
            color: "#5C32A7",
          }}
        >
          {isLoading ? (
            <Skeleton variant="rounded" sx={{ width: 180, height: 28, background: "#6B91D4" }} />
          ) : (
            user?.DisplayName || "Contestant"
          )}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "16px",
            color: "#7882AA",
          }}
        >
          {isLoading ? (
            <Skeleton variant="rounded" sx={{ width: 80, height: 16, background: "#6B91D4" }} />
          ) : (
            <>Level: {userData?.Data?.Player?.Level || 0}</>
          )}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "16px",
            color: "#7882AA",
          }}
        >
          {isLoading ? (
            <Skeleton variant="rounded" sx={{ width: 80, height: 16, background: "#6B91D4" }} />
          ) : (
            <> (Player ID: {userData?.Data?.Player?.UserId || ""}) </>
          )}
        </Typography>
      </Box>

      <Box sx={{ width: 1 }}>
        {isInventoryLoading
          ? Object.keys(commoditiesRenderingMap).map((k) => {
              return (
                <Skeleton
                  key={`skeleton-comm-${k}`}
                  variant="rounded"
                  sx={{ width: 1, height: 36, backgroundColor: "#6B91D4", mb: 2 }}
                />
              );
            })
          : Object.entries(commoditiesRenderingMap).map(([commKey, commData]) => {
              const commIcon = commData.icon;
              const commLabel = commData.label;
              return (
                <Box
                  key={`commodity-${commKey}`}
                  sx={{
                    width: 1,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 0.25,
                    pl: 7,
                    pr: 0.5,
                    border: "2px solid #7d17ac",
                    backgroundColor: "#6B2D91",
                    backgroundImage: "linear-gradient(90deg,#6b2d91 0%, #382053 100%)",
                    borderRadius: "8px",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "4px",
                      transform: "translateY(-52.5%) rotate(-15deg)",
                      width: "48px",
                      height: "48px",
                      filter: "drop-shadow(0 4px 2px #333a)",
                      ml: commKey === "Hints" ? 0.5 : 0,
                      mt: commKey === "Diamonds" ? -0.25 : 0,
                    }}
                  >
                    <img
                      src={commIcon}
                      style={{ maxHeight: "100%" }}
                      aria-label={`User ${commKey}`}
                    />
                  </Box>
                  <Box sx={{ flex: "1 0 auto" }}>
                    <Box sx={{ color: "#fff" }}>
                      <Typography
                        fontFamily="Lato"
                        sx={{ fontWeight: 400, fontSize: "14px", lineHeight: 1, mb: 0.15 }}
                      >
                        {commLabel}
                      </Typography>
                      <Typography
                        fontFamily="Inter"
                        sx={{ fontWeight: 900, fontSize: "16px", lineHeight: 1 }}
                      >
                        {formatNumber(commoditiesBalance[commKey as TCommodityKey] || 0)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    onClick={() => handleMoreCommoditiesClick(commKey as TCommodityKey)}
                    sx={{
                      fontFamily: "Inter",
                      minWidth: 0,
                      padding: 0,
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #88C4DF",
                      backgroundColor: "#6B91D4",
                      backgroundImage: "linear-gradient(180deg,#6b91d4 0%, #1b4081 100%)",
                      fontWeight: 900,
                      fontSize: "20px",
                    }}
                  >
                    <img width={12} src={iconPlus} alt="" />
                  </Button>
                </Box>
              );
            })}
      </Box>
    </Box>
  );
}
